import { Helmet } from "react-helmet-async";
import { Link, useParams, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ArrowRight, Calendar, Clock } from "lucide-react";
import Header from "@/components/sections/Header";
import Footer from "@/components/sections/Footer";
import { fadeUp } from "@/lib/motion";
import { getArticleBySlug, articles } from "@/lib/articles";

const SITE_URL = "https://psyloveyourself.com";

export default function Article() {
  const { slug } = useParams();
  const article = getArticleBySlug(slug);

  if (!article) return <Navigate to="/arthra" replace />;

  const url = `${SITE_URL}/arthra/${article.slug}`;
  const related = articles.filter((a) => a.slug !== article.slug).slice(0, 2);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: article.title,
    description: article.metaDescription,
    author: {
      "@type": "Person",
      name: "Άννα Παπαχρονόπουλου",
      url: SITE_URL,
    },
    publisher: {
      "@type": "Organization",
      name: "psyloveyourself",
      url: SITE_URL,
    },
    inLanguage: "el-GR",
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Αρχική", item: SITE_URL },
      { "@type": "ListItem", position: 2, name: "Άρθρα", item: `${SITE_URL}/arthra` },
      { "@type": "ListItem", position: 3, name: article.title, item: url },
    ],
  };

  const speakableSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    speakable: {
      "@type": "SpeakableSpecification",
      cssSelector: ["h1", "[data-testid='article-page'] p"],
    },
    url,
  };

  return (
    <>
      <Helmet>
        <html lang="el-GR" />
        <title>{`${article.title} · psyloveyourself`}</title>
        <meta name="description" content={article.metaDescription} />
        <meta name="keywords" content={article.keywords} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.metaDescription} />
        <meta property="og:url" content={url} />
        <meta property="og:locale" content="el_GR" />
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(breadcrumbSchema)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(speakableSchema)}
        </script>
      </Helmet>

      <Header />

      <main className="bg-background pt-32 pb-24" data-testid="article-page">
        <article className="max-w-[820px] mx-auto px-6 sm:px-12">
          <motion.div {...fadeUp}>
            <Link
              to="/arthra"
              className="inline-flex items-center gap-2 text-sm font-body text-muted-foreground hover:text-foreground transition-colors mb-10"
              data-testid="back-to-articles"
            >
              <ArrowLeft size={14} strokeWidth={1.5} />
              Όλα τα άρθρα
            </Link>

            <div className="flex items-center gap-3 mb-6">
              <span className="inline-flex text-[10px] tracking-[0.22em] uppercase font-body font-medium text-primary">
                {article.tag}
              </span>
              <span className="text-muted-foreground">·</span>
              <span className="inline-flex items-center gap-1.5 text-sm font-body text-muted-foreground">
                <Clock size={13} strokeWidth={1.5} />
                {article.readTime}
              </span>
            </div>

            <h1 className="font-heading font-medium tracking-tight text-foreground text-4xl sm:text-5xl lg:text-[60px] leading-[1.05]">
              {article.title}
            </h1>

            <p className="mt-6 font-body font-light text-lg sm:text-xl text-foreground/75 leading-relaxed">
              {article.excerpt}
            </p>

            <div className="mt-12 space-y-5 font-body font-light text-base sm:text-lg text-foreground/85 leading-relaxed">
              {article.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </motion.div>

          {/* CTA */}
          <motion.div
            {...fadeUp}
            transition={{ ...fadeUp.transition, delay: 0.1 }}
            className="mt-16 p-8 sm:p-10 bg-card rounded-3xl border border-border/50"
          >
            <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-3">
              Επικοινωνία
            </p>
            <h3 className="font-heading text-3xl sm:text-4xl text-foreground tracking-tight">
              Θες να μιλήσουμε <span className="italic font-light">από κοντά;</span>
            </h3>
            <p className="mt-3 font-body font-light text-foreground/75 leading-relaxed max-w-[480px]">
              Η πρώτη συνεδρία είναι ένας χώρος γνωριμίας. Μπορείς να κλείσεις
              ραντεβού δια ζώσης στην Πάτρα ή online.
            </p>
            <Link
              to="/#contact"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-foreground text-background px-7 py-3.5 font-body text-sm font-medium hover:-translate-y-0.5 transition-all duration-300"
              data-testid="article-cta"
            >
              Κλείσε συνεδρία
              <ArrowRight size={16} strokeWidth={1.5} />
            </Link>
          </motion.div>

          {/* Related */}
          {related.length > 0 && (
            <div className="mt-20 pt-12 border-t border-border/50">
              <p className="text-[11px] tracking-[0.25em] uppercase font-body font-medium text-primary mb-6">
                Διάβασε επίσης
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {related.map((r) => (
                  <Link
                    key={r.slug}
                    to={`/arthra/${r.slug}`}
                    className="group block bg-background border border-border/60 rounded-3xl p-7 hover:-translate-y-1 transition-all duration-500"
                    data-testid={`related-${r.slug}`}
                  >
                    <span className="text-[10px] tracking-[0.22em] uppercase font-body font-medium text-primary">
                      {r.tag}
                    </span>
                    <h4 className="mt-3 font-heading text-2xl text-foreground tracking-tight leading-tight">
                      {r.title}
                    </h4>
                    <span className="mt-4 inline-flex items-center gap-2 text-sm font-body text-foreground/80">
                      Διάβασέ το
                      <ArrowRight
                        size={14}
                        strokeWidth={1.5}
                        className="transition-transform group-hover:translate-x-0.5"
                      />
                    </span>
                  </Link>
                ))}
              </div>
            </div>
          )}
        </article>
      </main>

      <Footer />
    </>
  );
}
